import { useEffect, useState } from "react";
import { useNotification } from "../../../../components/notification/useNotification"
import useLazyLoading from "../../../../common/hooks/loader";
import Loader from "../../../../components/loader";
import IWalletDto from "../../../../interfaces/wallet/IWalletDto";
import { ICryptoCurrency } from "../../../../interfaces/crypto-currency/ICryptoCurrency";
import { useGlobalContext } from "../../../../context/global-context";
import { IProfitLossFiltersDto } from "../../../../interfaces/dashboard/IProfitLossFiltersDto";
import { tryGetAllWalletList } from "../../../../services/wallet/wallet-service";
import extractErrorMessage from "../../../../common/utils/errorMessages";
import { tryGetCryptoCurrencyListByUser } from "../../../../services/crypto-currency/cripto-currency.service";
import { useFormik } from "formik";
import { Button, Card, Checkbox, Grid, InputLabel, MenuItem, Select, Stack } from "@mui/material";

export default function ProfitLossFilters() {
    const { displayNotification } = useNotification();
    const [loading, setLoading] = useState(false);
    const showLoading = useLazyLoading(loading);
    const [walletList, setWalletList] = useState<IWalletDto[]>();
    const [cryptoCurrencyList, setCryptoCurrencyList] = useState<ICryptoCurrency[]>();
    const { dispatch, state } = useGlobalContext();
    const { profitLossFilters } = state;


    const formik = useFormik({
        initialValues: profitLossFilters,
        validationSchema: null,
        enableReinitialize: true,
        onSubmit: async (values) => {
            dispatch({ type: 'SET_PROFIT_LOSS_FILTERS', payload: values });
        }
    });

    async function getAllWalletList() {
        try {
            var response = await tryGetAllWalletList(profitLossFilters.isActiveWallet)

            if (response?.status === 200) {
                var walletList = response.data;
                setWalletList(walletList);
            }
        }
        catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
        }
    }

    async function getCryptoCurrencyListByUser() {
        try {
            var response = await tryGetCryptoCurrencyListByUser();

            if (response?.status === 200) {
                var cryptoCurrencyList = response.data;
                setCryptoCurrencyList(cryptoCurrencyList);
            }
        }
        catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
        }
    }

    useEffect(() => {
        setLoading(true);

        getAllWalletList();
        getCryptoCurrencyListByUser();

        setLoading(false);

    }, [profitLossFilters.isActiveWallet]);

    const handleCleanFilters = () => {
        dispatch({ type: 'RESET_PROFIT_LOSS_FILTERS' });
        formik.resetForm();
    }

    const handleIsActiveOnChange = (event: any) => {
        const value = event.target.value;

        if (value === 'true') {
            formik.setFieldValue("isActiveWallet", true);
        } else if (value === 'false') {
            formik.setFieldValue("isActiveWallet", false);
        }
    }

    return (
        <>
            {showLoading ? <Loader /> : false}
            <Card sx={{ mt: 2, mb: 2, border: '1px solid rgba(255,215,0,0.1)' }}>
                <form noValidate onSubmit={formik.handleSubmit}>
                    <Grid container spacing={3}>
                        <Grid sx={{ flex: 1 }}>
                            <Stack spacing={1} margin={2}>
                                <InputLabel sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                    Status Carteira
                                </InputLabel>
                                <Select
                                    value={formik.values.isActiveWallet}
                                    size="small"
                                    onChange={(event) => { handleIsActiveOnChange(event) }}
                                    MenuProps={{ disableScrollLock: true }}
                                >
                                    <MenuItem value={"true"} >Activa</MenuItem>
                                    <MenuItem value={"false"}>Inactiva</MenuItem>
                                </Select>
                            </Stack>
                        </Grid>
                        <Grid sx={{ flex: 1 }}>
                            <Stack spacing={1} margin={2}>
                                <InputLabel sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                    Carteiras
                                </InputLabel>
                                <Select
                                    multiple
                                    value={formik.values.walletIdList}
                                    size="small"
                                    onChange={(event) => { formik.setFieldValue("walletIdList", event.target.value) }}
                                    MenuProps={{ disableScrollLock: true }}
                                    renderValue={(selected) =>
                                        walletList?.filter((op) => selected.includes(op.id)).map((op) => op.walletName).join(", ")
                                    }
                                >
                                    {walletList && walletList.map((wallet) => (
                                        <MenuItem
                                            key={wallet.id}
                                            value={wallet.id}
                                        >
                                            <Checkbox checked={formik.values.walletIdList?.includes(wallet.id)} />
                                            {wallet.walletName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Stack>
                        </Grid>
                        <Grid sx={{ flex: 1 }}>
                            <Stack spacing={1} margin={2}>
                                <InputLabel sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                    Cripto Moedas
                                </InputLabel>
                                <Select
                                    multiple
                                    value={formik.values.cryptoCurrencyIdList}
                                    size="small"
                                    onChange={(event) => {
                                        formik.setFieldValue("cryptoCurrencyIdList", event.target.value);
                                    }}
                                    MenuProps={{ disableScrollLock: true }}
                                    renderValue={(selected) =>
                                        cryptoCurrencyList?.filter((op) => selected.includes(op.id)).map((op) => op.name).join(", ")
                                    }
                                >
                                    {cryptoCurrencyList && cryptoCurrencyList.map((cryptoCurrency) => (
                                        <MenuItem
                                            key={cryptoCurrency.id}
                                            value={cryptoCurrency.id}
                                        >
                                            <Grid container justifyContent="center" alignItems="center">
                                                <Checkbox checked={formik.values.cryptoCurrencyIdList?.includes(cryptoCurrency.id)} />
                                                <Grid container justifyContent="center" alignItems="center" spacing={0.5}>
                                                    <img src={cryptoCurrency.imageUrl} width="20" height="20" />
                                                    {cryptoCurrency.name}
                                                </Grid>
                                            </Grid>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Stack>
                        </Grid>
                    </Grid>
                    <Grid container justifyContent="flex-end" marginBottom={1} marginRight={2}>
                        <Grid marginRight={2}>
                            <Button
                                disableElevation
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() => handleCleanFilters()}
                            >
                                Limpar Filtros
                            </Button>
                        </Grid>
                        <Grid>
                            <Button
                                disableElevation
                                disabled={formik.isSubmitting}
                                size="small"
                                type="submit"
                                variant="contained"
                                color="primary"
                            >
                                Filtrar
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Card>
        </>
    )
}